import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { Card, Btn, Input, Select, Label, Table, Td, Empty } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { Building2, DoorOpen, Plus, Pencil, Archive, RotateCcw, BedDouble, Activity } from "lucide-react";
import { toast } from "sonner";
import RoomUtilization from "./RoomUtilization";

const FACILITY_TYPES = ["Outpatient", "Residential", "PHP", "IOP", "Detox", "Administrative"];
const ROOM_TYPES = ["Therapy Office", "Group Room", "Exam Room", "Telehealth Studio", "Medication Room", "Bedroom", "Common Area", "Office"];
const EMPTY_FAC = { name: "", type: "Outpatient", phone: "", address: "", city: "", state: "", zip: "", notes: "" };
const EMPTY_ROOM = { name: "", unit: "", floor: "", room_type: "Therapy Office", capacity: "", beds: "", amenities: "", notes: "" };

function FacilityForm({ form, setForm, onSave, busy }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={onSave} className="space-y-4">
      <div><Label>Facility name *</Label><Input data-testid="fac-name-input" value={form.name} onChange={f("name")} required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label>
          <Select data-testid="fac-type-select" value={form.type} onChange={f("type")}>
            {FACILITY_TYPES.map((t) => <option key={t}>{t}</option>)}
          </Select></div>
        <div><Label>Phone</Label><Input data-testid="fac-phone-input" value={form.phone} onChange={f("phone")} /></div>
      </div>
      <div><Label>Address</Label><Input data-testid="fac-address-input" value={form.address} onChange={f("address")} /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>City</Label><Input value={form.city} onChange={f("city")} /></div>
        <div><Label>State</Label><Input value={form.state} onChange={f("state")} /></div>
        <div><Label>ZIP</Label><Input value={form.zip} onChange={f("zip")} /></div>
      </div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={f("notes")} /></div>
      <div className="flex justify-end"><Btn data-testid="fac-save-btn" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Facility"}</Btn></div>
    </form>
  );
}

function RoomForm({ form, setForm, onSave, busy }) {
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={onSave} className="space-y-4">
      <div><Label>Room name / number *</Label><Input data-testid="room-name-input" value={form.name} onChange={f("name")} placeholder="e.g., Therapy Office 104" required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Unit</Label><Input data-testid="room-unit-input" value={form.unit} onChange={f("unit")} placeholder="e.g., Unit A" /></div>
        <div><Label>Floor</Label><Input data-testid="room-floor-input" value={form.floor} onChange={f("floor")} placeholder="e.g., 2" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Room type</Label>
          <Select data-testid="room-type-select" value={form.room_type} onChange={f("room_type")}>
            {ROOM_TYPES.map((t) => <option key={t}>{t}</option>)}
          </Select></div>
        <div><Label>Capacity</Label><Input data-testid="room-capacity-input" type="number" min="0" value={form.capacity} onChange={f("capacity")} /></div>
      </div>
      <div><Label>Beds (comma separated)</Label><Input data-testid="room-beds-input" value={form.beds} onChange={f("beds")} placeholder="e.g., 204-A, 204-B" /></div>
      <div><Label>Amenities</Label><Input value={form.amenities} onChange={f("amenities")} placeholder="e.g., Whiteboard, projector" /></div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={f("notes")} /></div>
      <div className="flex justify-end"><Btn data-testid="room-save-btn" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Room"}</Btn></div>
    </form>
  );
}

export default function FacilitiesRooms() {
  const [tab, setTab] = useState("directory");
  const [facilities, setFacilities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selFac, setSelFac] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [facPanel, setFacPanel] = useState(null);
  const [roomPanel, setRoomPanel] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/facilities", { params: { include_archived: true } }).then((r) => {
      setFacilities(r.data);
      setSelFac((s) => s || (r.data.find((x) => x.status === "active") || r.data[0] || {}).id || "");
    }).catch((e) => toast.error(apiError(e)));
    api.get("/rooms", { params: { include_archived: true } }).then((r) => setRooms(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const facRooms = useMemo(() => rooms.filter((r) => r.facility_id === selFac && (showArchived || r.status === "active")), [rooms, selFac, showArchived]);
  const selected = facilities.find((f) => f.id === selFac);

  const saveFac = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (facPanel.id) await api.put(`/facilities/${facPanel.id}`, facPanel.form);
      else await api.post("/facilities", facPanel.form);
      toast.success("Facility saved"); setFacPanel(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const saveRoom = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...roomPanel.form, facility_id: selFac };
      if (roomPanel.id) await api.put(`/rooms/${roomPanel.id}`, body);
      else await api.post("/rooms", body);
      toast.success("Room saved"); setRoomPanel(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await api.delete(confirm.kind === "facility" ? `/facilities/${confirm.id}` : `/rooms/${confirm.id}`);
      toast.success(`${confirm.kind === "facility" ? "Facility" : "Room"} archived`);
      setConfirm(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const restore = async (kind, id) => {
    try {
      await api.put(kind === "facility" ? `/facilities/${id}` : `/rooms/${id}`, { status: "active" });
      toast.success("Restored"); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="facilities-rooms-section">
      <div className="flex flex-wrap items-center gap-1 rounded-full border border-[#EFEBE0] bg-white p-1 w-fit">
        {[["directory", "Directory", DoorOpen], ["utilization", "Utilization", Activity]].map(([key, label, Icon]) => (
          <button key={key} data-testid={`facilities-tab-${key}`} onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
              tab === key ? "bg-[#4E342E] text-white" : "text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "utilization" ? <RoomUtilization /> : (
        <>
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold text-[#3E2723]">Facilities</h3>
            <p className="text-xs text-[#7B6157]">Locations your clinic operates. Rooms, floors, units, and beds live inside each facility, and feed the Scheduler Room View.</p>
          </div>
          <Btn data-testid="add-facility-btn" onClick={() => setFacPanel({ form: { ...EMPTY_FAC } })}><Plus size={14} /> Add Facility</Btn>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {facilities.map((f) => (
            <button key={f.id} data-testid={`fac-card-${f.id}`} onClick={() => setSelFac(f.id)}
              className={`rounded-2xl border p-4 text-left transition-colors ${selFac === f.id ? "border-[#4E342E] bg-[#F5F2EB]" : "border-[#EFEBE0] bg-white hover:bg-[#FDFCF8]"} ${f.status === "archived" ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 font-bold text-sm text-[#3E2723]"><Building2 size={15} className="text-[#7B6157]" /> {f.name}</div>
                <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">{f.type}</span>
              </div>
              <div className="mt-1 text-[11px] text-[#7B6157]">{[f.address, f.city].filter(Boolean).join(", ") || "No address"}</div>
              <div className="mt-2 flex items-center gap-2 text-[11px] font-semibold text-[#5D4037]">
                <DoorOpen size={12} /> {f.rooms_count} room{f.rooms_count === 1 ? "" : "s"}
                {f.status === "archived" && <span className="rounded bg-[#FFEBEE] px-1.5 py-px text-[10px] font-bold text-[#C62828]">Archived</span>}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {selected && (
        <Card className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-lg font-bold text-[#3E2723]">Rooms at {selected.name}</h3>
              <label className="mt-1 flex items-center gap-2 text-xs text-[#7B6157] cursor-pointer">
                <input type="checkbox" data-testid="rooms-show-archived" className="h-3.5 w-3.5 accent-[#4E342E]" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived rooms
              </label>
            </div>
            <div className="flex gap-2">
              <Btn variant="outline" data-testid="edit-facility-btn" onClick={() => setFacPanel({ id: selected.id, form: { ...EMPTY_FAC, ...selected } })}><Pencil size={13} /> Edit Facility</Btn>
              {selected.status === "active" ? (
                <Btn variant="ghost" data-testid="archive-facility-btn" className="text-[#C62828]" onClick={() => setConfirm({ kind: "facility", id: selected.id, name: selected.name })}><Archive size={13} /> Archive</Btn>
              ) : (
                <Btn variant="ghost" data-testid="restore-facility-btn" onClick={() => restore("facility", selected.id)}><RotateCcw size={13} /> Restore</Btn>
              )}
              <Btn data-testid="add-room-btn" onClick={() => setRoomPanel({ form: { ...EMPTY_ROOM } })}><Plus size={14} /> Add Room</Btn>
            </div>
          </div>
          {facRooms.length === 0 ? <Empty text="No rooms yet. Add the first room for this facility." /> : (
            <Table headers={["Room", "Unit", "Floor", "Type", "Capacity", "Beds", "Status", ""]}>
              {facRooms.map((r) => (
                <tr key={r.id} data-testid={`room-row-${r.id}`} className={`border-b border-[#EFEBE0] last:border-0 ${r.status === "archived" ? "opacity-50" : ""}`}>
                  <Td className="font-semibold text-[#3E2723]">{r.name}</Td>
                  <Td>{r.unit || "-"}</Td>
                  <Td>{r.floor || "-"}</Td>
                  <Td>{r.room_type || "-"}</Td>
                  <Td>{r.capacity || "-"}</Td>
                  <Td>{(r.beds || []).length > 0 ? <span className="flex items-center gap-1 text-xs"><BedDouble size={12} className="text-[#7B6157]" /> {r.beds.join(", ")}</span> : "-"}</Td>
                  <Td><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "active" ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFEBEE] text-[#C62828]"}`}>{r.status}</span></Td>
                  <Td>
                    <div className="flex justify-end gap-1">
                      <button data-testid={`edit-room-${r.id}`} onClick={() => setRoomPanel({ id: r.id, form: { ...EMPTY_ROOM, ...r, beds: (r.beds || []).join(", ") } })}
                        className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Pencil size={14} /></button>
                      {r.status === "active" ? (
                        <button data-testid={`archive-room-${r.id}`} onClick={() => setConfirm({ kind: "room", id: r.id, name: r.name })}
                          className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Archive size={14} /></button>
                      ) : (
                        <button data-testid={`restore-room-${r.id}`} onClick={() => restore("room", r.id)}
                          className="rounded-lg p-1.5 text-[#2E7D32] hover:bg-[#E8F5E9]"><RotateCcw size={14} /></button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}
        </>
      )}

      <SlidePanel open={!!facPanel} onClose={() => setFacPanel(null)} title={facPanel?.id ? "Edit Facility" : "New Facility"} testId="facility-panel">
        {facPanel && <FacilityForm form={facPanel.form} setForm={(fn) => setFacPanel((s) => ({ ...s, form: typeof fn === "function" ? fn(s.form) : fn }))} onSave={saveFac} busy={busy} />}
      </SlidePanel>
      <SlidePanel open={!!roomPanel} onClose={() => setRoomPanel(null)} title={roomPanel?.id ? "Edit Room" : `New Room at ${selected?.name || ""}`} testId="room-panel">
        {roomPanel && <RoomForm form={roomPanel.form} setForm={(fn) => setRoomPanel((s) => ({ ...s, form: typeof fn === "function" ? fn(s.form) : fn }))} onSave={saveRoom} busy={busy} />}
      </SlidePanel>
      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={doArchive} busy={busy}
        title={`Archive ${confirm?.kind}`} description={`"${confirm?.name}" will be hidden from the Scheduler Room View and booking forms. Existing appointments keep their room history.`}
        confirmWord="ARCHIVE" confirmLabel="Archive" warning="You can restore it later from this page." />
    </div>
  );
}
